/**
 * MARKETPLACE-HIDDEN-1 — a section that is gone on purpose says so.
 *
 * A menu with a thing missing and a menu with a thing broken look exactly the
 * same from outside. This file is the difference. Every section listed here is
 * absent from the menu deliberately, with the ruling that put it there, the
 * date it was ruled, and where its pages still are — so a session or a person
 * who finds it missing reads a decision rather than a defect, and knows which
 * one line brings it back.
 *
 * NOTHING IS DELETED. The routes and the pages stay in the codebase exactly as
 * they were. Hiding is a gate returning false; rebuilding is removing an entry
 * from the list below and putting the old gate back.
 */

export interface DeadSection {
  /** The menu node id, the same id `account_navigation` rows are keyed on. */
  id: string
  /** The day the owner ruled it, as a date somebody can go and look up. */
  ruled: string
  /** What was ruled, in the owner's own terms. */
  ruling: string
  /** Where the pages still are, so "nothing was deleted" is checkable. */
  pagesStay: string
  /** The one change that brings it back. */
  toBringItBack: string
}

/**
 * THE SECTIONS THAT ARE HIDDEN ON PURPOSE.
 *
 * One entry, and it should stay short: a list of dead sections that grows is a
 * product nobody finished rather than a product somebody shaped.
 */
export const HIDDEN_ON_PURPOSE: ReadonlyArray<DeadSection> = [
  {
    id: 'marketplace',
    ruled: '2026-09-09',
    ruling:
      'The Feature Marketplace is a dead section, hidden on purpose, and may be rebuilt later.',
    pagesStay:
      'src/pages/dashboard/settings/ModuleLibraryPage.tsx, routed at /dashboard/marketplace, with src/lib/marketplace.ts and its tests',
    toBringItBack: 'remove this entry and give the section back the account gate it had',
  },
]

/** The whole reason a section is missing, or null when it is not one of these. */
export function hiddenOnPurpose(id: string): DeadSection | null {
  return HIDDEN_ON_PURPOSE.find((s) => s.id === id) ?? null
}

/**
 * THE CONDITION THE MENU ASKS.
 *
 * It takes no role and no width because it answers the same for all of them:
 * a section that is gone is gone for everybody. A gate that asked about a role
 * would be a section that is merely restricted, which is a different thing and
 * would read as one.
 */
export function sectionIsHidden(id: string): boolean {
  return hiddenOnPurpose(id) !== null
}

/** Said in full, for a person reading a guard's failure or a record. */
export function whyItIsMissing(id: string): string | null {
  const dead = hiddenOnPurpose(id)
  if (dead === null) return null
  return `The ${dead.id} section is hidden on purpose, ruled by the owner on ${dead.ruled}: ${dead.ruling} Nothing was deleted — ${dead.pagesStay}. To bring it back, ${dead.toBringItBack}.`
}
